<template>
  <div>
    <div class="adm-toolbar">
      <div class="adm-tabs" role="tablist" aria-label="Filter cities">
        <button v-for="tab in tabs" :key="tab.value" type="button" class="adm-tab" role="tab"
                :class="{ 'is-active': filter === tab.value }" :aria-selected="filter === tab.value" @click="filter = tab.value">
          {{ tab.label }} <span class="adm-tab__count">{{ tab.count }}</span>
        </button>
      </div>
      <div class="adm-toolbar__spacer"></div>
      <div class="adm-search">
        <i class="fas fa-search"></i>
        <input v-model.trim="search" class="adm-input" placeholder="Search city or state" aria-label="Search cities" />
      </div>
      <button type="button" class="adm-btn adm-btn--primary" @click="openForm()">
        <i class="fas fa-plus"></i> Add city
      </button>
    </div>

    <div class="adm-card">
      <div class="adm-table-wrap">
        <table class="adm-table">
          <thead>
            <tr>
              <th>City</th>
              <th>State</th>
              <th>Country</th>
              <th>Status</th>
              <th class="adm-col-actions">Actions</th>
            </tr>
          </thead>
          <tbody>
            <tr v-if="loading && !cities.length">
              <td colspan="5"><div class="adm-skeleton" style="width: 40%"></div></td>
            </tr>
            <tr v-else-if="!filtered.length">
              <td colspan="5"><div class="adm-empty"><i class="fas fa-city"></i><strong>No cities found</strong></div></td>
            </tr>
            <template v-else>
              <tr v-for="city in filtered" :key="city.city_id">
                <td class="adm-strong">{{ city.name }}</td>
                <td>{{ city.state || "—" }}</td>
                <td>{{ city.country || "—" }}</td>
                <td>
                  <span class="adm-badge" :class="city.is_active ? 'adm-badge--active' : 'adm-badge--inactive'">
                    {{ city.is_active ? "Active" : "Hidden" }}
                  </span>
                </td>
                <td class="adm-col-actions">
                  <button type="button" class="adm-icon-btn" title="Edit" aria-label="Edit city" @click="openForm(city)">
                    <i class="fas fa-pen"></i>
                  </button>
                  <button type="button" class="adm-btn adm-btn--sm" :disabled="saving" @click="toggle(city)">
                    {{ city.is_active ? "Hide" : "Show" }}
                  </button>
                </td>
              </tr>
            </template>
          </tbody>
        </table>
      </div>
    </div>
    <p class="adm-muted adm-cities-note">
      Hiding a city only removes it from the booking and search dropdowns. Doctors, clinics and bookings in it are untouched.
    </p>

    <admin-modal v-if="form" :title="form.city_id ? 'Edit city' : 'Add city'" size="sm" :busy="saving" @close="form = null">
      <div v-if="formError" class="adm-alert adm-alert--error"><i class="fas fa-exclamation-circle"></i><div>{{ formError }}</div></div>
      <div class="adm-field">
        <label class="adm-label" for="city-name">City name <span class="adm-req">*</span></label>
        <input id="city-name" v-model="form.name" class="adm-input" maxlength="100" placeholder="Udupi" />
      </div>
      <div class="adm-field">
        <label class="adm-label" for="city-state">State</label>
        <input id="city-state" v-model="form.state" class="adm-input" maxlength="100" placeholder="Karnataka" />
      </div>
      <div class="adm-field">
        <label class="adm-label" for="city-country">Country</label>
        <input id="city-country" v-model="form.country" class="adm-input" maxlength="100" />
      </div>
      <template #footer>
        <button type="button" class="adm-btn" :disabled="saving" @click="form = null">Cancel</button>
        <button type="button" class="adm-btn adm-btn--primary" :disabled="saving" @click="save">
          <span v-if="saving" class="adm-spinner"></span> Save city
        </button>
      </template>
    </admin-modal>
  </div>
</template>

<script>
import axios from "axios";
import AdminModal from "./AdminModal.vue";
import { errorMessage } from "../admin-utils";

export default {
  name: "CitiesPanel",
  // The shell passes every panel the same props; ignore the ones this panel does not use.
  inheritAttrs: false,
  components: { AdminModal },
  props: {
    cities: { type: Array, default: () => [] },
    loading: { type: Boolean, default: false },
  },
  emits: ["notify", "cities-changed"],
  data() {
    return { search: "", filter: "all", form: null, formError: "", saving: false };
  },
  computed: {
    tabs() {
      const active = this.cities.filter(c => c.is_active).length;
      return [
        { value: "all", label: "All", count: this.cities.length },
        { value: "active", label: "Active", count: active },
        { value: "hidden", label: "Hidden", count: this.cities.length - active },
      ];
    },
    filtered() {
      const term = this.search.toLowerCase();
      return this.cities.filter(city => {
        if (this.filter === "active" && !city.is_active) return false;
        if (this.filter === "hidden" && city.is_active) return false;
        return !term || city.name.toLowerCase().includes(term) || (city.state || "").toLowerCase().includes(term);
      });
    },
  },
  methods: {
    openForm(city = null) {
      this.formError = "";
      this.form = city
        ? { city_id: city.city_id, name: city.name, state: city.state || "", country: city.country || "India" }
        : { city_id: null, name: "", state: "", country: "India" };
    },
    async save() {
      const name = this.form.name.trim().replace(/\s+/g, " ");
      if (!name) {
        this.formError = "Enter a city name.";
        return;
      }
      const payload = { name, state: this.form.state.trim() || null, country: this.form.country.trim() || "India" };
      this.saving = true;
      try {
        if (this.form.city_id) await axios.put(`/cities/${this.form.city_id}`, payload);
        // POST returns the existing city when the name already exists.
        else await axios.post("/cities", payload);
        this.$emit("notify", `${name} was saved.`);
        this.form = null;
        this.$emit("cities-changed");
      } catch (error) {
        this.formError = errorMessage(error, "Could not save the city.");
      } finally {
        this.saving = false;
      }
    },
    async toggle(city) {
      this.saving = true;
      try {
        await axios.patch(`/cities/${city.city_id}/status`, null, { params: { is_active: !city.is_active } });
        this.$emit("notify", `${city.name} is now ${city.is_active ? "hidden" : "visible"}.`);
        this.$emit("cities-changed");
      } catch (error) {
        this.$emit("notify", errorMessage(error, "Could not update the city."), "error");
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>

<style scoped>
.adm-cities-note {
  font-size: 12px;
  margin: 12px 4px 0;
}
</style>
